<template>
  <section id="simul-form">
    <ValidationObserver v-slot="{ handleSubmit }">
      <form @submit.prevent="handleSubmit(simul ? updateSimul : createSimul)">
        <div class="is-flex is-align-content-flex-start">
          <ValidationProvider
            rules="max:30"
            v-slot="{ errors }"
            name="Simul name"
          >
            <b-field
              label="Simul name"
              :type="errors.length ? 'is-danger' : ''"
              :message="errors.length ? errors[0] : ''"
            >
              <b-input placeholder="Name" v-model="simulName"></b-input>
            </b-field>
          </ValidationProvider>
        </div>

        <div class="is-flex mt-4">
          <ValidationProvider
            rules="min_value:5|max_value:60"
            v-slot="{ errors }"
            name="Base time control"
          >
            <b-field
              label="Base Time Control (minutes)"
              :type="errors.length ? 'is-danger' : ''"
              :message="errors.length ? errors[0] : ''"
            >
              <b-numberinput
                controls-alignment="right"
                controls-position="compact"
                min="5"
                step="5"
                v-model="baseTimeControl"
              ></b-numberinput>
            </b-field>
          </ValidationProvider>
        </div>

        <div class="is-flex mt-4">
          <ValidationProvider
            rules="min_value:2|max_value:15"
            v-slot="{ errors }"
            name="Player limit"
          >
            <b-field
              label="Player Limit"
              :type="errors.length ? 'is-danger' : ''"
              :message="errors.length ? errors[0] : ''"
            >
              <b-numberinput
                controls-alignment="right"
                controls-position="compact"
                min="2"
                :step="1"
                :min-step="1"
                v-model="playerLimit"
              ></b-numberinput>
            </b-field>
          </ValidationProvider>
        </div>

        <div class="is-flex mt-4">
          <ValidationProvider
            :rules="`min_value:0|max_value:${validMaxRating}`"
            v-slot="{ errors }"
            name="Min rating"
          >
            <b-field
              label="Min Rating"
              :type="errors.length ? 'is-danger' : ''"
              :message="errors.length ? errors[0] : ''"
            >
              <b-numberinput
                :controls="false"
                min="0"
                v-model="minRating"
              ></b-numberinput>
            </b-field>
          </ValidationProvider>

          <ValidationProvider
            :rules="`min_value:${validMinRating}|max_value:9999`"
            v-slot="{ errors }"
            name="Max rating"
          >
            <b-field
              label="Max Rating"
              class="ml-3"
              :type="errors.length ? 'is-danger' : ''"
              :message="errors.length ? errors[0] : ''"
            >
              <b-numberinput
                :controls="false"
                min="0"
                v-model="maxRating"
              ></b-numberinput>
            </b-field>
          </ValidationProvider>
        </div>

        <div class="is-flex mt-4">
          <ValidationProvider
            rules="required"
            v-slot="{ errors }"
            name="Start time"
          >
            <b-field
              label="Start date and time"
              :type="errors.length ? 'is-danger' : ''"
              :message="errors.length ? errors[0] : ''"
            >
              <b-datetimepicker
                style="width: 199px"
                placeholder="Click to select..."
                icon="calendar"
                locale="en-AU"
                v-model="startTime"
                horizontal-time-picker
                :min-datetime="new Date()"
              ></b-datetimepicker>
            </b-field>
          </ValidationProvider>

          <ValidationProvider
            rules="required"
            v-slot="{ errors }"
            name="Privacy"
          >
            <b-field
              label="Privacy"
              style="width: 199px"
              class="ml-3"
              :type="errors.length ? 'is-danger' : ''"
              :message="errors.length ? errors[0] : ''"
            >
              <b-select v-model="privacy" expanded>
                <option value="Private">Private (invite only)</option>
                <option value="Public">Public</option>
              </b-select>
            </b-field>
          </ValidationProvider>
        </div>

        <div class="is-flex mt-4">
          <b-button
            native-type="submit"
            :label="simul ? 'Save' : 'Create'"
            type="is-primary"
            :loading="isLoading"
          />
          <b-button
            class="ml-4"
            label="Cancel"
            type="is-secondary"
            @click="handleCancel"
          />
        </div>
      </form>
    </ValidationObserver>
  </section>
</template>

<script>
import { SIMUL_STATUS } from "vue-chess-shared/constants";
import { firestore } from "@/services/firebase";
import { mapGetters } from "vuex";
import { ONLINE_STATUS } from "@/constants";

export default {
  name: "SimulForm",

  props: {
    simul: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      isLoading: false,
      simulName: null,
      baseTimeControl: 15,
      playerLimit: 2,
      minRating: null,
      maxRating: null,
      privacy: "Public",
      startTime: new Date(),
    };
  },

  watch: {
    simul() {
      if (this.simul.status === SIMUL_STATUS.STARTED) {
        // go back if simum is not editable
        this.$router.back();
        return;
      }

      this.simulName = this.simul.simulName;
      this.baseTimeControl = this.simul.baseTimeControl;
      this.playerLimit = this.simul.playerLimit;
      this.minRating = this.simul.minRating;
      this.maxRating = this.simul.maxRating;
      this.privacy = this.simul.privacy;
      this.startTime = this.simul.startTime.toDate();
    },
  },

  computed: {
    ...mapGetters({
      currentPlayer: "getPlayer",
      onlineStatus: "getOnlineStatus",
    }),

    validMinRating() {
      return this.minRating || 0;
    },

    validMaxRating() {
      return this.maxRating || 99999;
    },
  },

  methods: {
    createSimul() {
      if (this.onlineStatus === ONLINE_STATUS.PLAYING) {
        this.$buefy.toast.open({
          message: "Cannot host a simul while in a tournament!",
          type: "is-warning",
        });
        return;
      }

      this.isLoading = true;
      firestore
        .collection("simuls")
        .add({
          userId: this.currentPlayer.user_id,
          simulName: this.simulName,
          owner: this.currentPlayer,
          baseTimeControl: this.baseTimeControl,
          playerLimit: this.playerLimit,
          minRating: this.minRating,
          maxRating: this.maxRating,
          startTime: this.startTime,
          privacy: this.privacy,
          status: SIMUL_STATUS.CREATED,
          createdAt: new Date(),
        })
        .then((doc) => {
          this.isLoading = false;
          this.$router.replace({
            name: "simul",
            params: { simulId: doc.id },
          });
        });
    },
    updateSimul() {
      if (this.simul.players && this.simul.players.length > this.playerLimit) {
        this.$buefy.toast.open({
          message: "Cannot reduce the player limit. Too many players joined!",
          type: "is-danger",
          duration: 10000,
        });
        return;
      }

      this.isLoading = true;

      firestore
        .collection("simuls")
        .doc(this.simul.id)
        .update({
          userId: this.currentPlayer.user_id,
          simulName: this.simulName,
          owner: this.currentPlayer,
          baseTimeControl: this.baseTimeControl,
          playerLimit: this.playerLimit,
          minRating: this.minRating,
          maxRating: this.maxRating,
          startTime: this.startTime,
          privacy: this.privacy,
          status: SIMUL_STATUS.CREATED,
          updatedAt: new Date(),
        })
        .then(() => {
          this.isLoading = false;
          this.$router.replace({
            name: "simul",
            params: { simulId: this.simul.id },
          });
        });
    },

    handleCancel() {
      if (this.simul && this.simul.id) {
        this.$router.replace({ name: "simul", simulId: this.simul.id });
      } else {
        this.$router.replace({ name: "simuls" });
      }
    },
  },
};
</script>

<style scoped></style>
